import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import QUERIES, { Main, Midi, Full } from '../theming/styles';
import styled, { createGlobalStyle } from 'styled-components';
import { Nav } from '../components';
import { SiteFooter } from '../components/foot/SiteFooter';
import { Spread } from '../components/switch/styles';
import OutlineDubi from '../components/OutlineDubi';
import ProblemSection from '../components/ProblemSection';
import ZoomImg from '../components/ZoomImg';
import { AwardImg, DubPreview } from '../images';
import CompetitorImg from '../images/dubi/Dubi_idea_cpmpetitor.png';
import FlowImg from '../images/dubi/Dubi_idea_flow.png';
import FeaturesImg from '../images/dubi/Dubi_idea_features.png';

const ImagePlaceholder = styled.div`
    width: 100%;
    background: rgba(255,255,255,0.03);
    border: 2px dashed rgba(255,255,255,0.10);
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 24px;
    gap: 10px;
    min-height: ${({ $height }) => $height || '320px'};
    margin: ${({ $margin }) => $margin || '32px 0'};
`;

const PlaceholderLabel = styled.p`
    font-family: var(--font-body);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.25;
    margin: 0;
`;

const PlaceholderTitle = styled.p`
    font-family: var(--font-body);
    font-size: 13px;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.4;
    text-align: center;
    margin: 0;
`;

const FlexGroup = styled.div`
    margin: 40px 0;
    display: flex;
    justify-content: space-between;
    gap: 30px;
    flex-wrap: wrap;
    background-color: rgb(255 255 255 / 40%);
    padding: 30px 30px;
    border-radius: 10px;

    div {
        width: 100%;

        @media (${QUERIES.medium}) {
            width: 45%;
        }
    }

    @media (${QUERIES.large}) {
        flex-wrap: nowrap;
        gap: 50px;
        padding: 30px 50px;
        margin: 60px 0;
    }
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-body);
    font-size: 14px;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.5;
    margin-bottom: 32px;
    transition: opacity 0.15s ease;
    &:hover { opacity: 1; }
`;

const GlobalStyle = createGlobalStyle`
  html {
    scroll-behavior: smooth;
  }
`;

const CaseHeader = styled.div`
    max-width: 1200px;
    margin: 0 auto;
`;

/* ── Reusable: 3-up principle cards ── */
const PrincipleGrid = styled.div`
    display: flex;
    gap: 8px;
    background: var(--color-surface);
    border-radius: 18px;
    padding: 8px;
    margin: 20px 0;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const PrincipleCard = styled.div`
    flex: 1;
    background: var(--color-bg);
    border-radius: 12px;
    padding: 20px;
`;

/* ── Reusable: persona / requirement cards ── */
const PersonaGrid = styled.div`
    display: flex;
    gap: 16px;
    margin: 24px 0;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const PersonaCard = styled.div`
    flex: 1;
    background: var(--color-surface);
    border-radius: 16px;
    padding: 28px;
`;

const PersonaLabel = styled.p`
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 500;
    opacity: 0.45;
    margin: 20px 0 8px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
`;

const PersonaCheck = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 8px;
`;

const AwardCard = styled.div`
    background: #0d1b5e;
    border-radius: 20px;
    padding: 32px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    position: relative;
    overflow: hidden;
    margin-bottom: 40px;

    @media (max-width: 600px) {
        flex-direction: column;
        padding: 24px 20px;
        gap: 16px;
        text-align: center;
    }
`;

const AwardLaurel = styled.svg`
    flex-shrink: 0;
    opacity: 0.35;

    @media (max-width: 600px) {
        display: none;
    }
`;

const AwardCenter = styled.div`
    flex: 1;
    text-align: center;
`;

const AwardTitle = styled.div`
    color: #4ade80;
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 26px;
    line-height: 1.2;
    letter-spacing: -0.5px;
    margin-bottom: 14px;

    @media (max-width: 600px) {
        font-size: 20px;
        margin-bottom: 10px;
    }
`;

const AwardSub = styled.div`
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    opacity: 0.9;

    @media (max-width: 600px) {
        font-size: 14px;
    }
`;

const AwardPhotoWrap = styled.div`
    flex-shrink: 0;
    width: 220px;
    height: 150px;
    border-radius: 12px;
    overflow: hidden;
    cursor: zoom-in;
    position: relative;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
    }

    @media (max-width: 600px) {
        width: 100%;
        height: 160px;
    }
`;

const PageLayout = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: flex-start;
    padding: 0 20px;
`;

const NavColumn = styled.div`
    width: 18%;
    flex-shrink: 0;
    position: sticky;
    top: 90px;
    align-self: flex-start;
    padding-top: 52px;

    @media (max-width: 1099px) {
        display: none;
    }
`;

const ContentColumn = styled.div`
    flex: 1;
    min-width: 0;

    h4 {
        font-weight: 500;
    }
`;

const CASE_STUDIES = [
    {
        tags: ['UX Design', 'Safaricom PLC'],
        title: 'Empowering the Backbone of Trade: The My Safaricom Partner Portal Redesign',
        desc: 'Unifying 5 fragmented portals into one scalable SaaS platform for 4,700+ dealers — driving 90M KES in orders within the first month.',
        href: '/partner-portal',
        bg: '#dff0ea',
        preview: require('../images/schedule/thumb.png'),
    },
    {
        tags: ['Design System', 'Safaricom PLC'],
        title: 'From Fragmentation to a System 100+ People Actually Use',
        desc: "How I led the rebuild of Safaricom's design system — unifying tokens, components, and documentation across products.",
        href: '/dira',
        bg: '#e4ecfb',
        preview: require('../images/dira/dira-cover-image.png'),
    },
];

const BrowserCard = ({ cs, style, isBack }) => (
    <div className="browser-card" style={{
        background: '#fff',
        borderRadius: 20,
        padding: '32px',
        display: 'flex',
        gap: 32,
        alignItems: 'stretch',
        minHeight: 340,
        boxSizing: 'border-box',
        boxShadow: '0 8px 40px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)',
        ...style,
    }}>
        {/* Browser mockup */}
        <div className="browser-mockup" style={{
            flex: '0 0 45%',
            background: '#fff',
            borderRadius: 12,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 24px rgba(0,0,0,0.09)',
        }}>
            <div style={{
                background: '#f0f0f0',
                padding: '9px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                borderBottom: '1px solid #e5e5e5',
                flexShrink: 0,
            }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57', display: 'inline-block' }} />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e', display: 'inline-block' }} />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840', display: 'inline-block' }} />
                <div style={{ flex: 1, marginLeft: 8, background: '#e2e2e2', borderRadius: 5, height: 20, display: 'flex', alignItems: 'center', paddingLeft: 8 }}>
                    <span style={{ fontSize: 9, opacity: 0.5, fontFamily: 'var(--font-body)', letterSpacing: 0 }}>kimanigodfrey.bio{cs.href}</span>
                </div>
            </div>
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                {cs.preview && (
                    <img src={cs.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
                )}
            </div>
        </div>
        {/* Text */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ background: 'rgba(3,14,73,0.1)', color: '#030e49', borderRadius: 100, padding: '4px 14px', fontSize: 11.5, fontWeight: 600, fontFamily: 'var(--font-body)' }}>{cs.tags[0]}</span>
                <span style={{ fontSize: 12, opacity: 0.4, fontFamily: 'var(--font-body)' }}>{cs.tags[1]}</span>
            </div>
            <h3 style={{ margin: 0, lineHeight: 1.2, fontSize: 'clamp(16px,2vw,22px)' }}>{cs.title}</h3>
            <p className="text-style" style={{ margin: 0, fontSize: 13 }}>{cs.desc}</p>
            <a href={cs.href} className="btn-primary">
                Read case study
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </a>
        </div>
    </div>
);

const MoreCaseStudies = () => {
    const [order, setOrder] = useState([0, 1]);

    const shuffle = () => setOrder(prev => [...prev.slice(1), prev[0]]);

    const OFFSET_Y = 46;     // px each back card peeks above
    const SCALE_STEP = 0.04; // scale reduction per level back
    const numCards = order.length;
    const peekHeight = (numCards - 1) * OFFSET_Y;

    return (
        <div className="case-deck-wrap" style={{
            background: 'var(--color-surface)',
            borderRadius: 24,
            padding: '50px',
        }}>
            {/* Deck container */}
            <div style={{ position: 'relative', paddingTop: peekHeight }}>
                {/* Invisible spacer sets the container height */}
                <div style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                    <BrowserCard cs={CASE_STUDIES[0]} />
                </div>

                {/* All cards absolutely positioned; keyed by csIdx so they animate on order change */}
                {order.map((csIdx, stackPos) => {
                    const ty = -stackPos * OFFSET_Y;
                    const scale = 1 - stackPos * SCALE_STEP;
                    const zIdx = numCards - stackPos;
                    return (
                        <div
                            key={csIdx}
                            style={{
                                position: 'absolute',
                                top: peekHeight,
                                left: 0,
                                right: 0,
                                zIndex: zIdx,
                                transform: `translateY(${ty}px) scale(${scale})`,
                                transformOrigin: 'bottom center',
                                transition: 'transform 400ms ease-out',
                            }}
                        >
                            <BrowserCard cs={CASE_STUDIES[csIdx]} />
                        </div>
                    );
                })}
            </div>

            {/* Shuffle button */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                <button onClick={shuffle} className="btn-text">
                    Shuffle
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>
                </button>
            </div>
        </div>
    );
};

const Dubi = ({ toggleMode, mode, spread, setDisableScroll }) => {
    const spreadClass =
        spread === 'first'
            ? 'growBlack'
            : spread === 'second'
            ? 'growWhite'
            : '';

    const [modalImg, setModalImg] = useState(null);

    return (
        <Main style={{
            position: 'relative',
            overflow: setDisableScroll ? 'hidden' : 'null',
            height: setDisableScroll ? '100vh' : 'unset',
        }}>
            <GlobalStyle />
            <Spread className={spreadClass} />
            <Nav mode={mode} toggleMode={toggleMode} />

            <PageLayout>
                <NavColumn>
                    <OutlineDubi />
                </NavColumn>
                <ContentColumn>

            <CaseHeader id="case-study-header" className="card-pad" style={{ maxWidth: 'none', margin: 0 }}>
                <BackLink to="/">← Back</BackLink>
                <h2 id="summary" style={{ marginBottom: 28 }}>
                    Turning Scattered Feedback Into 4X Faster Decisions For Teams, Powering Better Products And Experiences.
                </h2>

                <p className="text-style" style={{ marginBottom: 16 }}>
                    As the Lead Product Designer, I drove the end-to-end design of Dubi — a unified survey and feedback platform that shipped across Kenya and Ethiopia
                </p>
                <p className="text-style" style={{ marginBottom: 32 }}>
                    By connecting <a href="#journey-nps" style={{ color: "var(--color-primary)", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}>automated journey surveys</a> and remove manual audience campaigns creation into one simple experience, Dubi empowers teams to gather insights from over 50M+ users and transform <a href="#impact" style={{ color: "var(--color-primary)", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}>real-time conversations into data-driven decisions</a>.
                </p>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", background: "var(--color-surface)", borderRadius: 18, padding: 8, margin: "32px 0" }}>

                    {/* ── Key Results card ── */}
                    <div style={{ flex: 1, minWidth: 180, background: "var(--color-bg)", borderRadius: 12, padding: "20px" }}>
                        <div style={{ color: "var(--color-text)", marginBottom: 20, opacity: 0.6 }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                            </svg>
                        </div>
                        <h5 style={{ marginBottom: 10 }}>Key Results</h5>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {[
                                "Delivered a unified survey platform for all teams.",
                                "Reduced manual survey setup time.",
                                "Strengthened feedback visibility for product decisions.",
                            ].map(item => (
                                <div key={item} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                                        <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" opacity="0.4"/>
                                        <path d="M6.5 10.5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <p className="text-style" style={{ margin: 0 }}>{item}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Timeline card ── */}
                    <div style={{ flex: 1, minWidth: 180, background: "var(--color-bg)", borderRadius: 12, padding: "20px" }}>
                        <div style={{ color: "var(--color-text)", marginBottom: 20, opacity: 0.6 }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                        </div>
                        <h5 style={{ marginBottom: 10 }}>Timeline</h5>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {[
                                { year: "2024,", phase: "Discovery & Research" },
                                { year: "2024,", phase: "Design & Build" },
                                { year: "2024,", phase: "MVP Launch — Kenya & Ethiopia" },
                            ].map(({ year, phase }) => (
                                <p key={phase} className="text-style" style={{ margin: 0 }}>
                                    <strong style={{ fontWeight: 600 }}>{year}</strong>{" "}
                                    <span style={{ opacity: 0.5 }}>{phase}</span>
                                </p>
                            ))}
                        </div>
                    </div>

                    {/* ── My Role + Methods card ── */}
                    <div style={{ flex: 1, minWidth: 180, background: "var(--color-bg)", borderRadius: 12, padding: "20px" }}>
                        <div style={{ color: "var(--color-text)", marginBottom: 20, opacity: 0.6 }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
                            </svg>
                        </div>
                        <h5 style={{ marginBottom: 10 }}>My Role</h5>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 20 }}>
                            <p className="text-style" style={{ margin: 0 }}>Lead Product Designer</p>
                            <p className="text-style" style={{ margin: 0 }}>End-to-End Design</p>
                            <p className="text-style" style={{ margin: '8px 0 0', fontSize: 14, opacity: 0.7 }}>I led the design end to end: research, flows, prioritisation and UI. Alex Karanja (UX Lead) reviewed direction; engineering built it.</p>
                        </div>
                        <h5 style={{ marginBottom: 10 }}>Methods</h5>
                        <p className="text-style" style={{ margin: 0 }}>
                            User Research / User Journey Mapping / Competitive Analysis / Visual Design / Prototyping / Testing
                        </p>
                    </div>

                </div>

                <a
                    href="https://dubi.safaricom.co.ke/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                        background: "var(--color-text, #0d0d2b)",
                        color: "#fff",
                        borderRadius: 999,
                        padding: "12px 20px 12px 24px",
                        fontSize: 15,
                        fontWeight: 600,
                        textDecoration: "none",
                        marginBottom: 48,
                    }}
                >
                    Live Preview
                    <span style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 30,
                        height: 30,
                        background: "rgba(255,255,255,0.15)",
                        borderRadius: "50%",
                    }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 12L12 2M12 2H5M12 2v7" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                </a>

                <h3 style={{ marginBottom: 16 }}>What's the big deal?</h3>
                <p className="text-style" style={{ marginBottom: 32 }}>
                    After launch in Kenya and Ethiopia, the project bagged 1st position during the SAFARICOM 2024 GROUP CEO AWARD Under the Best Innovation (Customer Obsession Pilar)
                </p>

                {/* ── AWARD CARD ── */}
                <AwardCard>
                    <AwardLaurel width="90" height="140" viewBox="0 0 90 140" fill="none">
                        <path d="M45 10 C30 25 10 35 8 55 C6 75 20 85 35 80 C20 90 8 105 12 120 C16 135 32 138 45 130" stroke="#8899cc" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                        <path d="M45 10 C38 30 25 40 20 58 C15 76 25 88 38 85" stroke="#8899cc" strokeWidth="2" fill="none" strokeLinecap="round"/>
                        <path d="M45 10 C42 32 32 44 28 62 C24 78 32 90 43 88" stroke="#8899cc" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                        <ellipse cx="18" cy="58" rx="10" ry="6" fill="#8899cc" opacity="0.5" transform="rotate(-20 18 58)"/>
                        <ellipse cx="12" cy="78" rx="10" ry="6" fill="#8899cc" opacity="0.4" transform="rotate(-30 12 78)"/>
                        <ellipse cx="16" cy="98" rx="10" ry="6" fill="#8899cc" opacity="0.4" transform="rotate(-20 16 98)"/>
                        <ellipse cx="24" cy="42" rx="9" ry="5" fill="#8899cc" opacity="0.45" transform="rotate(-15 24 42)"/>
                        <ellipse cx="30" cy="26" rx="8" ry="4" fill="#8899cc" opacity="0.35" transform="rotate(-10 30 26)"/>
                    </AwardLaurel>

                    <AwardCenter>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16 }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" opacity="0.8">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                            </svg>
                            <span style={{ color: "#fff", fontSize: 16, fontWeight: 500, opacity: 0.85 }}>Awards</span>
                        </div>
                        <AwardTitle>
                            SAFARICOM<br />2024 GROUP CEO AWARD
                        </AwardTitle>
                        <AwardSub>Best innovation (customer obsession Pillar)</AwardSub>
                    </AwardCenter>

                    <AwardPhotoWrap className="img-tooltip-wrap" onClick={() => setModalImg(AwardImg)}>
                        <img src={AwardImg} alt="Safaricom 2024 Group CEO Award" />
                        <span className="img-tooltip">Tap to view</span>
                    </AwardPhotoWrap>

                    <AwardLaurel width="90" height="140" viewBox="0 0 90 140" fill="none" style={{ transform: "scaleX(-1)" }}>
                        <path d="M45 10 C30 25 10 35 8 55 C6 75 20 85 35 80 C20 90 8 105 12 120 C16 135 32 138 45 130" stroke="#8899cc" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                        <path d="M45 10 C38 30 25 40 20 58 C15 76 25 88 38 85" stroke="#8899cc" strokeWidth="2" fill="none" strokeLinecap="round"/>
                        <path d="M45 10 C42 32 32 44 28 62 C24 78 32 90 43 88" stroke="#8899cc" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                        <ellipse cx="18" cy="58" rx="10" ry="6" fill="#8899cc" opacity="0.5" transform="rotate(-20 18 58)"/>
                        <ellipse cx="12" cy="78" rx="10" ry="6" fill="#8899cc" opacity="0.4" transform="rotate(-30 12 78)"/>
                        <ellipse cx="16" cy="98" rx="10" ry="6" fill="#8899cc" opacity="0.4" transform="rotate(-20 16 98)"/>
                        <ellipse cx="24" cy="42" rx="9" ry="5" fill="#8899cc" opacity="0.45" transform="rotate(-15 24 42)"/>
                        <ellipse cx="30" cy="26" rx="8" ry="4" fill="#8899cc" opacity="0.35" transform="rotate(-10 30 26)"/>
                    </AwardLaurel>
                </AwardCard>

                <p className="text-style" style={{ marginTop: 0 }}>
                    Before Dubi, Safaricom's teams measured customer sentiment with <mark>spreadsheets, custom scripts, and tribal knowledge</mark>. Campaigns took days to launch and insights arrived after the moment had passed.
                </p>
            </CaseHeader>

            <Full>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <ZoomImg
                        src={DubPreview}
                        alt="Dubi survey & feedback platform"
                        onClick={() => setModalImg(DubPreview)}
                    />
                    <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>Final shipped design of the Dubi survey &amp; feedback platform</p>
                </div>
            </Full>

            {/* ── IMPACT NUMBERS ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h3 id="results-at-a-glance" style={{ margin: "0 0 24px" }}>Results at a Glance</h3>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", background: "var(--color-surface)", borderRadius: 18, padding: 8 }}>
                    {[
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
                            label: "Journey NPS Engagements",
                            body: "92M+ conversations in the first 3 months"
                        },
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
                            label: "Users Reached",
                            body: "51M+ across Kenya & Ethiopia in 90 days"
                        },
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
                            label: "Survey Responses",
                            body: "4.8M manual audience responses collected"
                        },
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
                            label: "Surveys Created",
                            body: "604 on launch — 507 via the simple template path"
                        },
                    ].map(({ icon, label, body }) => (
                        <div key={label} style={{ flex: 1, minWidth: 180, background: "var(--color-bg)", borderRadius: 12, padding: "20px" }}>
                            <div style={{ color: "var(--color-text)", marginBottom: 20, opacity: 0.6 }}>{icon}</div>
                            <h5 style={{ marginBottom: 10 }}>{label}</h5>
                            <p className="text-style">{body}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── THE PROBLEM ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h3 id="the-problem" style={{ margin: "0 0 24px" }}>
                    The Problem: Drowning in Data and Tools
                </h3>
                <blockquote style={{ borderLeft: "3px solid var(--color-primary)", padding: "10px 20px", margin: "24px 0", fontStyle: "italic", color: "var(--color-primary)", fontFamily: "var(--font-display)", fontSize: "18px", letterSpacing: "-0.3px" }}>
                    Seven tools. No single source of truth. Insights that arrived days too late.
                </blockquote>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    Safaricom was scaling across two countries and still measuring customer sentiment with Google Forms, Typeform, and ad-hoc spreadsheets. The data existed and the tools existed — but the teams were <mark>drowning in both.</mark>
                </p>
                <p className="text-style">
                    Through stakeholder workshops we identified <mark>three critical friction points:</mark>
                </p>
                <ProblemSection />
            </div>

            {/* ── PROBLEM STATEMENT ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h4 id="the-design-challenge" style={{ margin: "0 0 24px" }}>
                    Problem Statement
                </h4>
                <blockquote style={{ borderLeft: "3px solid var(--color-primary)", padding: "10px 20px", margin: "24px 0", fontStyle: "italic", color: "var(--color-primary)", fontFamily: "var(--font-display)", fontSize: "18px", letterSpacing: "-0.3px" }}>
                    Safaricom's internal teams had no reliable way to collect customer feedback at scale — survey creation was slow, manual, and completely disconnected from real user journeys, leaving product decisions driven by instinct rather than evidence.
                </blockquote>
                <p className="text-style">
                    Teams across Kenya and Ethiopia were using fragmented tools — Google Forms, Typeform, ad-hoc spreadsheets — with no shared infrastructure and no way to trigger surveys automatically at key customer touchpoints. <mark>The result: insights that arrived days too late, campaigns that took hours to launch, and a feedback loop that simply didn't close.</mark>
                </p>
            </div>

            {/* ── DESIGN PROCESS ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h3 id="discovery" style={{ margin: "0 0 24px" }}>
                    Design Process: Start, Ship, Learn, Loop
                </h3>
                <p className="text-style" style={{ marginBottom: 20 }}>
                    No double diamond, no agile ceremony. We had a tight clock and a blank canvas, so we moved fast and validated as we went. The work centred on three threads: <mark>discovery, competitive analysis, and ruthless prioritization.</mark>
                </p>

                <PrincipleGrid>
                    {[
                        {
                            icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><path d="M6 21V9a9 9 0 0 0 9 9"/></svg>,
                            label: "Improvise",
                            body: "The clock was ticking, so our best bet was to get started first, then build on top iterating on the fly.",
                        },
                        {
                            icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="18" r="3"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="12" r="3"/><path d="M18 9v6M6 12h9"/></svg>,
                            label: "Iterate",
                            body: "Each sprint surfaced a new constraint. We shipped thin, gathered signal, and layered complexity only where teams actually needed it.",
                        },
                        {
                            icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
                            label: "Adapt",
                            body: "Things were unpredictable — insights kept us going, helping us adapt through different problems across two markets.",
                        },
                    ].map(({ icon, label, body }) => (
                        <PrincipleCard key={label}>
                            <div style={{ color: "var(--color-text)", marginBottom: 16, opacity: 0.6 }}>{icon}</div>
                            <h5 style={{ marginBottom: 10, fontWeight: 700 }}>{label}</h5>
                            <p className="text-style" style={{ margin: 0 }}>{body}</p>
                        </PrincipleCard>
                    ))}
                </PrincipleGrid>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", background: "var(--color-surface)", borderRadius: 18, padding: 8, margin: "20px 0" }}>
                    {[
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
                            label: "Discovery",
                            body: "Workshops with PMs, researchers, marketing leads, developers, and executives across both markets — pinpointing campaign creation and the missing insights layer as the costliest friction."
                        },
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
                            label: "Competitive Analysis",
                            body: "Mapped Typeform, SurveyMonkey, Qualtrics, and Medallia. The gap wasn't features — it was journey integration inside a telco ecosystem. That became our differentiation."
                        },
                    ].map(({ icon, label, body }) => (
                        <div key={label} style={{ flex: 1, minWidth: 220, background: "var(--color-bg)", borderRadius: 12, padding: "20px" }}>
                            <div style={{ color: "var(--color-text)", marginBottom: 20, opacity: 0.6 }}>{icon}</div>
                            <h5 style={{ marginBottom: 10 }}>{label}</h5>
                            <p className="text-style">{body}</p>
                        </div>
                    ))}
                </div>

                <h4 id="how-might-we" style={{ margin: "24px 0 16px" }}>
                    How Might We
                </h4>
                <p className="text-style" style={{ marginBottom: 16 }}>
                    Two framing questions anchored every decision:
                </p>
                <PrincipleGrid>
                    {[
                        { label: "User lens", body: "How might we make feedback feel personal and connected to real product moments, so users feel heard — not surveyed?" },
                        { label: "Business lens", body: "How might we reduce campaign launch time so dramatically that teams survey by default, not as an afterthought?" },
                    ].map(({ label, body }) => (
                        <PrincipleCard key={label}>
                            <h5 style={{ color: "var(--color-primary)", marginBottom: 10 }}>{label}</h5>
                            <p className="text-style" style={{ margin: 0 }}>{body}</p>
                        </PrincipleCard>
                    ))}
                </PrincipleGrid>
            </div>

            {/* ── WHO NEEDS HELP ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h3 id="who-needs-help" style={{ margin: "0 0 16px" }}>Who needs Help?</h3>
                <p className="text-style" style={{ marginBottom: 8 }}>
                    During discovery, our goal was to understand what teams across Safaricom needed from a survey and feedback system. Through stakeholder workshops and interviews, we identified key user requirements that guided Dubi's design direction:
                </p>
                <p className="text-style" style={{ marginBottom: 24, fontWeight: 600 }}>Requirements and Persona</p>

                <PrincipleGrid>
                    <PrincipleCard>
                        <h5 style={{ marginBottom: 16, lineHeight: 1.4 }}>Product Managers, Researchers &amp; Marketing Teams</h5>
                        <PersonaLabel>Requirement</PersonaLabel>
                        <PersonaCheck>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                                <circle cx="10" cy="10" r="9" stroke="var(--color-primary)" strokeWidth="1.5"/>
                                <path d="M6.5 10.5l2.5 2.5 4.5-4.5" stroke="var(--color-primary)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <p className="text-style" style={{ margin: 0 }}>A unified, easy-to-use platform to launch, track, and analyze targeted surveys</p>
                        </PersonaCheck>
                        <PersonaLabel>Insight</PersonaLabel>
                        <PersonaCheck>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                                <circle cx="10" cy="10" r="9" stroke="var(--color-primary)" strokeWidth="1.5"/>
                                <path d="M6.5 10.5l2.5 2.5 4.5-4.5" stroke="var(--color-primary)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <p className="text-style" style={{ margin: 0 }}>Teams needed a single source of truth for feedback collection, segmentation, and measuring feature or campaign success without relying on multiple external tools.</p>
                        </PersonaCheck>
                    </PrincipleCard>

                    <PrincipleCard>
                        <h5 style={{ marginBottom: 16, lineHeight: 1.4 }}>Developers, Executives &amp; Analysts</h5>
                        <PersonaLabel>Requirement</PersonaLabel>
                        <PersonaCheck>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                                <circle cx="10" cy="10" r="9" stroke="var(--color-primary)" strokeWidth="1.5"/>
                                <path d="M6.5 10.5l2.5 2.5 4.5-4.5" stroke="var(--color-primary)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <p className="text-style" style={{ margin: 0 }}>Seamless integration and real-time analytics dashboards</p>
                        </PersonaCheck>
                        <PersonaLabel>Insight</PersonaLabel>
                        <PersonaCheck>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                                <circle cx="10" cy="10" r="9" stroke="var(--color-primary)" strokeWidth="1.5"/>
                                <path d="M6.5 10.5l2.5 2.5 4.5-4.5" stroke="var(--color-primary)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <p className="text-style" style={{ margin: 0 }}>Effortless survey triggers within journeys and instant access to engagement, NPS, and performance data to make faster, data-driven decisions.</p>
                        </PersonaCheck>
                    </PrincipleCard>
                </PrincipleGrid>
            </div>

            {/* ── COMPETITIVE ANALYSIS ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h3 id="competitive-analysis" style={{ margin: "0 0 16px" }}>Competitive Analysis</h3>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    I conducted a competitive analysis to identify commonalities among competitors and gather valuable insights for building our product. This allowed me to differentiate ourselves and provided a basis for product development.
                </p>
                <ZoomImg
                    src={CompetitorImg}
                    alt="Dubi competitive analysis"
                    onClick={() => setModalImg(CompetitorImg)}
                />
                <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>
                    Competitive analysis mapping Dubi against key survey platforms — identifying gaps, risks, and differentiation opportunities
                </p>

                <h5 style={{ marginTop: 40, marginBottom: 12 }}>User Flows</h5>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    I created a visual user flow to align with the feature functionality before designing the interface. This also contributes to a smoother navigation experience, helping users accomplish tasks efficiently.
                </p>
                <ZoomImg
                    src={FlowImg}
                    alt="Dubi user flow diagram"
                    onClick={() => setModalImg(FlowImg)}
                />
                <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>
                    User flow mapping the end-to-end journey for survey creation, audience targeting, and results review
                </p>

                <h5 style={{ marginTop: 40, marginBottom: 12 }}>Feature Prioritization</h5>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    We sorted out ideas through an Eisenhower Matrix. (also called the Urgent-Important Matrix) is a prioritization framework that helps teams and individuals decide what to focus on first — based on urgency and importance.
                </p>
                <ZoomImg
                    src={FeaturesImg}
                    alt="Dubi feature prioritization matrix"
                    onClick={() => setModalImg(FeaturesImg)}
                />
                <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>
                    Eisenhower Matrix used to prioritize Dubi features by urgency and importance — guiding what shipped in MVP
                </p>
            </div>

            {/* ── THE PRODUCT ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h3 id="building-mvp" style={{ margin: "0 0 24px" }}>
                    The Solution
                </h3>

                <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', opacity: 0.45, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                    Let's get to work
                </p>
                <h4 style={{ margin: "0 0 16px" }}>The MVP</h4>
                <p className="text-style" style={{ marginBottom: 32 }}>
                    With all the necessary foundations, knowledge, and preparation in place, I began to design the interface for dubi. We prioritized the creation of surveys through templates with ready questions since it was faster to implement. The design aimed to add make the whole process of creating, sharing and gathering responses a simple and straight forward without compromising efficiency and prioritize user-centricity.
                </p>

                <h5 style={{ margin: "0 0 16px" }}>Landing Page</h5>
                <video
                    src={require('../images/dubi/Record202511130001.mp4')}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ width: '100%', borderRadius: 16, display: 'block', marginBottom: 8 }}
                />
                <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "0 0 48px" }}>Dubi landing page — survey creation and template selection flow</p>

                {/* ── 2×2 Feature grid — collapses to 1 col on mobile ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>

                    {/* Card 1 — Ideaboards */}
                    <div style={{ background: 'var(--color-surface)', borderRadius: 18, padding: 'clamp(16px,3vw,24px)', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <p className="text-style" style={{ margin: 0, fontSize: 'clamp(14px,2vw,16px)' }}>
                            A centralized hub for managing all ideaboards. Including a quick board creation and user engagement.
                        </p>
                        <ZoomImg
                            src={require('../images/dubi/ideaboard_mockup.jpg')}
                            alt="Ideaboards — centralized board management"
                            onClick={() => setModalImg(require('../images/dubi/ideaboard_mockup.jpg'))}
                            style={{ borderRadius: 12, overflow: 'hidden' }}
                            radius="12px"
                        />
                        <p style={{ fontSize: 13, opacity: 0.5, textAlign: 'center', margin: '-4px 0 0' }}>Ideaboards — centralized board management</p>
                    </div>

                    {/* Card 2 — Analytics Dashboard */}
                    <div style={{ background: 'var(--color-surface)', borderRadius: 18, padding: 'clamp(16px,3vw,24px)', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <p className="text-style" style={{ margin: 0, fontSize: 'clamp(14px,2vw,16px)' }}>
                            A summary dashboard where users can view summaries, track responses and detailed analytics.
                        </p>
                        <ZoomImg
                            src={require('../images/dubi/survey_dashbaoard.png')}
                            alt="Analytics dashboard — response summaries & metrics"
                            onClick={() => setModalImg(require('../images/dubi/survey_dashbaoard.png'))}
                            style={{ borderRadius: 12, overflow: 'hidden', background: '#f6f8f4' }}
                            radius="12px"
                        />
                        <p style={{ fontSize: 13, opacity: 0.5, textAlign: 'center', margin: '-4px 0 0' }}>Analytics dashboard — response summaries & metrics</p>
                    </div>

                    {/* Card 3 — Survey Builder */}
                    <div style={{ background: 'var(--color-surface)', borderRadius: 18, padding: 'clamp(16px,3vw,24px)', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <p className="text-style" style={{ margin: 0, fontSize: 'clamp(14px,2vw,16px)' }}>
                            A user-friendly interface with drag-and-drop functionality and pre-build questions.
                        </p>
                        <ZoomImg
                            src={require('../images/dubi/dubi_form_builder.png')}
                            alt="Survey builder — drag-and-drop question editor"
                            onClick={() => setModalImg(require('../images/dubi/dubi_form_builder.png'))}
                            style={{ borderRadius: 12, overflow: 'hidden' }}
                            radius="12px"
                        />
                        <p style={{ fontSize: 13, opacity: 0.5, textAlign: 'center', margin: '-4px 0 0' }}>Survey builder — drag-and-drop question editor</p>
                    </div>

                    {/* Card 4 — Templates (stacked) */}
                    <div style={{ background: 'var(--color-surface)', borderRadius: 18, padding: 'clamp(16px,3vw,24px)', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <p className="text-style" style={{ margin: 0, fontSize: 'clamp(14px,2vw,16px)' }}>
                            Pre-made templates with pre-set questions that match each type of survey.
                        </p>
                        <div style={{ position: 'relative', height: 260 }}>
                            <ZoomImg
                                src={require('../images/dubi/blank.png')}
                                alt="Blank template"
                                onClick={() => setModalImg(require('../images/dubi/blank.png'))}
                                style={{ position: 'absolute', top: 0, left: '0%', width: '75%', borderRadius: 10, zIndex: 1 }}
                            />
                            <ZoomImg
                                src={require('../images/dubi/net.png')}
                                alt="NPS template"
                                onClick={() => setModalImg(require('../images/dubi/net.png'))}
                                style={{ position: 'absolute', top: 44, left: '12%', width: '75%', borderRadius: 10, zIndex: 2 }}
                            />
                            <ZoomImg
                                src={require('../images/dubi/emotion.png')}
                                alt="Emotion template"
                                onClick={() => setModalImg(require('../images/dubi/emotion.png'))}
                                style={{ position: 'absolute', top: 88, left: '22%', width: '75%', borderRadius: 10, zIndex: 3 }}
                            />
                        </div>
                        <p style={{ fontSize: 13, opacity: 0.5, textAlign: 'center', margin: '0' }}>Survey templates — NPS &amp; Emotion feedback forms</p>
                    </div>

                </div>
            </div>

            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h4 id="journey-nps" style={{ margin: "0 0 16px" }}>
                    2. Journey NPS (Automated Triggers)
                </h4>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    The engine behind 92M engagements. Surveys fire automatically when a user completes a touchpoint — an M-PESA transaction, SIM activation, or data bundle purchase — with <mark>zero manual intervention after setup.</mark> This was the unlock that made scale possible.
                </p>
                <video
                    src={require('../images/dubi/dubi-survey.mp4')}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ width: '100%', borderRadius: 16, display: 'block', marginBottom: 8 }}
                />
                <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "0 0 0" }}>Journey NPS — event-triggered survey configuration</p>
            </div>

            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h4 id="segmentation" style={{ margin: "0 0 16px" }}>
                    3. Audience Upload & Segmentation
                </h4>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    Upload lists of 51M+ users and segment by product, region, or behavioral cohort. Pre- and post-campaign surveys close the loop on marketing effectiveness — <mark>one screen replaced three tools and four manual steps.</mark>
                </p>
                <ZoomImg
                    src={require('../images/dubi/audience.avif')}
                    alt="Audience upload & segmentation flow"
                    onClick={() => setModalImg(require('../images/dubi/audience.avif'))}
                    radius="16px"
                />
                <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>Audience upload & segmentation flow</p>
            </div>

            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h4 style={{ margin: "0 0 16px" }}>
                    4. Analytics Dashboard
                </h4>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    A single source of truth for all survey activity: real-time response tracking, NPS trend lines, engagement rates, and an executive summary view. Powered by <mark>Microsoft Clarity and Metabase</mark> post-launch.
                </p>
                <ZoomImg
                    src={require('../images/dubi/survey_dashbaoard.png')}
                    alt="Analytics dashboard — real-time response & NPS trends"
                    onClick={() => setModalImg(require('../images/dubi/survey_dashbaoard.png'))}
                    radius="16px"
                />
                <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>Analytics dashboard — real-time response & NPS trends</p>
            </div>

            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h4 style={{ margin: "0 0 16px" }}>Idea Board</h4>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    A simple Internal Ideaboard for collecting and organising internal thoughts — track problems, insights, or options during a project or team retros.
                </p>
                <video
                    src={require('../images/dubi/ideaboard.mp4')}
                    autoPlay loop muted playsInline
                    style={{ width: '100%', borderRadius: 16, display: 'block', marginBottom: 8 }}
                />
                <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "0 0 0" }}>Idea Board — internal ideation and team retro tool</p>
            </div>

            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h4 style={{ margin: "0 0 16px" }}>Touch Point (Channel)</h4>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    A touch point (channel) is any interaction or point of contact between a user and a product, service, or brand. We created an idea board to map out all potential touch points, such as website visits, mobile app notifications, customer support calls, social media interactions, and in-store experiences.
                </p>
                <video
                    src={require('../images/dubi/dubi-channels.mp4')}
                    autoPlay loop muted playsInline
                    style={{ width: '100%', borderRadius: 16, display: 'block', marginBottom: 8 }}
                />
                <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "0 0 0" }}>Touch Point channels — mapping user interaction points</p>
            </div>

            {/* ── IMPACT ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h4 id="impact" style={{ margin: "0 0 16px" }}>
                    The Impact: By the Numbers
                </h4>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    In the first three months, Dubi exceeded every projection.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                    {[
                        { label: "92M+", body: "Journey NPS engagements across M-PESA, data bundles, and SIM flows — zero manual campaigns" },
                        { label: "51M+", body: "users reached across Kenya and Ethiopia in 90 days" },
                        { label: "4.8M", body: "manual survey responses from targeted audience campaigns" },
                        { label: "604 surveys", body: "created on launch, with 507 via the simple template path" },
                    ].map(({ label, body }) => (
                        <div key={label} style={{ background: 'var(--color-surface)', borderRadius: 18, padding: 'clamp(20px,3vw,28px) clamp(16px,2.5vw,24px)' }}>
                            <p style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(26px,4vw,40px)', fontWeight: 700, color: 'var(--color-primary)', margin: '0 0 8px', letterSpacing: 0 }}>{label}</p>
                            <p className="text-style" style={{ margin: 0, fontSize: 'clamp(13px,1.5vw,16px)' }}>{body}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── USERS REVIEWS ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', opacity: 0.45, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    Testimonials
                </p>
                <h3 style={{ margin: "0 0 12px" }}>Users Reviews</h3>
                <p className="text-style" style={{ marginBottom: 32 }}>
                    Real feedback from the people who use Dubi every day — teams across Kenya and Ethiopia who went from manual spreadsheets to automated, data-driven decisions.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                    {[
                        {
                            label: "Safaricom Ethiopia · Sep 2024",
                            body: '"Automated surveys saved our teams hours, produced reliable signals, and helped us track shifts across journeys with ease."'
                        },
                        {
                            label: "Safaricom Kenya · Nov 2024",
                            body: '"Audience uploads feel smooth even with huge lists. Pre and post campaign surveys help me catch drop-offs early."'
                        },
                    ].map(({ label, body }) => (
                        <div key={label} style={{ background: 'var(--color-surface)', borderRadius: 18, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <p className="text-style" style={{ margin: 0, fontStyle: 'italic' }}>{body}</p>
                            <p style={{ margin: 0, fontSize: 12, fontWeight: 600, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── LESSONS LEARNED ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h4 id="learnings" style={{ margin: "0 0 16px" }}>
                    Lessons Learned
                </h4>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    This project reinforced a core belief: <mark>the best feature in a high-volume enterprise tool is one that disappears after configuration.</mark>
                </p>
                <ul style={{ listStyle: "none", margin: "20px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                        { label: "Automation beats features", body: "Journey NPS required no user action after setup — and that's exactly why it drove 92M engagements." },
                        { label: "Templates drive adoption, not training", body: "507 of 604 surveys used the simple path. Make the first success fast; everything else is noise." },
                        { label: "Process is context-dependent", body: "Discovery was non-negotiable; ceremony was optional. The skill was knowing which to honor and which to compress." },
                        { label: "What I'd do differently", body: "Push stronger segmentation into V1 — bulk uploads worked, but targeting was too blunt, a gap I should have caught in discovery." },
                    ].map(({ label, body }) => (
                        <li key={label} style={{ display: "flex", gap: 16, alignItems: "flex-start", background: "var(--color-surface)", borderRadius: 10, padding: "16px 20px" }}>
                            <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", background: "var(--color-primary)", marginTop: 7 }} />
                            <span className="text-style" style={{ margin: 0 }}>
                                <strong style={{ color: "var(--color-primary)", display: "block", marginBottom: 4 }}>{label}</strong>
                                {body}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* ── WHAT'S NEXT ── */}
            <div className="card-pad" style={{ marginBottom: 0 }}>
                <h4 id="next-steps" style={{ margin: "0 0 16px" }}>
                    What's Next
                </h4>
                <ul style={{ listStyle: "none", margin: "20px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                        { label: "Feedback Triage System", body: "A centralized queue where teams assign ownership, track resolution, and close the loop with users." },
                        { label: "AI-Powered Insights Agent", body: "Surface patterns across 92M+ signals automatically — a weekly digest of what changed and why, without opening a dashboard." },
                        { label: "Advanced Segmentation", body: "Behavioral cohorts, cross-journey filtering, and A/B survey routing to match the depth of the dataset." },
                    ].map(({ label, body }) => (
                        <li key={label} style={{ display: "flex", gap: 16, alignItems: "flex-start", background: "var(--color-surface)", borderRadius: 10, padding: "16px 20px" }}>
                            <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", background: "var(--color-primary)", marginTop: 7 }} />
                            <span className="text-style" style={{ margin: 0 }}>
                                <strong style={{ color: "var(--color-primary)", display: "block", marginBottom: 4 }}>{label}</strong>
                                {body}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* ── THE TEAM ── */}
            <div className="card-pad" style={{ marginBottom: 0, padding: 0, margin: 0 }}>
                <h4 id="the-team" style={{ margin: "0 0 16px" }}>
                    The Team
                </h4>
                <FlexGroup>
                    <div>
                        <p className="text-style">
                            Godfrey Kimani — Product Designer, Lead<br />
                            Alex Karanja — UX Lead<br />
                            Clinton Wekesa — Product Owner<br />
                            Stephen Maina — Tech Lead<br />
                            Christopher Muiruri — Frontend Developer
                        </p>
                    </div>
                    <div>
                        <p className="text-style">
                            Gloria Mutie — Backend Developer<br />
                            Nehemiah Cheburet — Solution Architect<br />
                            Moses Mutua — Business Analyst<br />
                            Francis Gituku — Research & Planning
                        </p>
                    </div>
                </FlexGroup>
            </div>

            {/* ── MORE CASE STUDIES ── */}
            <div className="card-pad more-case-studies-wrap" style={{ marginBottom: 150 }}>
                <h3 style={{ margin: "0 0 32px" }}>More case studies</h3>
                <MoreCaseStudies />
            </div>

                </ContentColumn>
            </PageLayout>

            {/* ── IMAGE MODAL ── */}
            {modalImg && (
                <div
                    onClick={() => setModalImg(null)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        background: "rgba(0,0,0,0.6)",
                        padding: 24,
                        animation: "fadeIn 0.2s ease",
                    }}
                >
                    <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}} @keyframes scaleIn{from{opacity:0;transform:scale(0.88)}to{opacity:1;transform:scale(1)}}`}</style>
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            position: "relative",
                            maxWidth: "80vw",
                            maxHeight: "80vh",
                            animation: "scaleIn 0.25s cubic-bezier(0.34,1.56,0.64,1)",
                        }}
                    >
                        <img
                            src={modalImg}
                            alt="Award"
                            style={{
                                display: "block",
                                maxWidth: "100%",
                                maxHeight: "80vh",
                                borderRadius: 16,
                                boxShadow: "0 32px 80px rgba(0,0,0,0.5)",
                                objectFit: "contain",
                            }}
                        />
                        <button
                            onClick={() => setModalImg(null)}
                            style={{
                                position: "absolute",
                                top: -16,
                                right: -16,
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                background: "#fff",
                                border: "none",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                                fontSize: 18,
                                color: "#111",
                                lineHeight: 1,
                            }}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            <SiteFooter />
        </Main>
    );
};

export { Dubi };
